import { type WorkspacePostingsApi } from '@/features/opportunities/api/workspace-postings-api'
import { collection } from '@/infra/collection'
import { WorkspacePostingOpportunity } from './WorkspacePostingOpportunity'
import { mapWorkspacePostingOpportunityResourceToProps } from './workspace-posting-opportunity-mappers'
import type { CursorPaged, CursorPageQuery } from '@/infra/paging'
import { Id } from '@/infra/id'
import type { FetchUserDataFn } from '@/features/iam'
import { once } from 'lodash-es'
import { firstValueFrom, type Observable } from 'rxjs'
import { type User } from '@/features/iam/users/User'
import { take, switchMap } from 'rxjs/operators'
import { shareLatest } from '@/utils/rx-util'

/**
 * Lists workspace posting opportunities.
 */
export type ListWorkspacePostingOpportunitiesFn = (
  query: CursorPageQuery,
  signal?: AbortSignal,
) => Promise<CursorPaged<WorkspacePostingOpportunity>>

/**
 * Loads and caches the workspace posting opportunities a Pro can apply to.
 */
export class WorkspacePostingOpportunityStore {
  /**
   * Opportunities collection.
   */
  readonly opportunities = collection({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new WorkspacePostingOpportunity(),
  })

  /**
   * Observable that, when first subscribed to, probes whether we can view opportunities.
   */
  readonly canViewOpportunities$

  constructor(
    private readonly _api: WorkspacePostingsApi,
    private readonly _fetchUserData: FetchUserDataFn,
    private readonly _currentUser$: Observable<User>,
  ) {
    this.canViewOpportunities$ = this._currentUser$
      .pipe(
        take(1),
        switchMap(() => this.canViewWorkspaceOpportunities()),
      )
      .pipe(shareLatest())

    // Probes as soon as there is a user.
    this.canViewOpportunities$.subscribe()
  }

  /**
   * Whether the current user may view workspace postings, which only Pros can.
   */
  readonly canViewWorkspaceOpportunities = once(async () => {
    // Callers may probe before the session has a user, and the result is
    // cached for the session.
    const user = await firstValueFrom(this._currentUser$)
    try {
      const userData = await this._fetchUserData(user.publicId)
      return userData.userDataType === 'Pro'
    } catch {
      return false
    }
  })

  /**
   * Lists a page of opportunities.
   *
   * @param query
   * @param signal
   */
  async listOpportunities(
    query: CursorPageQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<WorkspacePostingOpportunity>> {
    const response = await this._api.opportunities.list(
      { after: query.after, limit: query.limit },
      signal,
    )
    return {
      data: this.opportunities.set(
        response.data.map(mapWorkspacePostingOpportunityResourceToProps),
      ),
      next: response.cursor,
      total: response.total,
    }
  }

  /**
   * Applies to the opportunity.
   *
   * @param opportunityId
   */
  async applyToOpportunity(opportunityId: Id) {
    const opportunity = this.opportunities.get(opportunityId)
    if (!opportunity) {
      return
    }
    await this._api.opportunities.apply(Id.assert(opportunityId)).then(() => {
      opportunity.set({
        isApplicant: true,
      })
    })
  }

  /**
   * Withdraws the application from the opportunity.
   *
   * @param opportunityId
   */
  async withdrawFromOpportunity(opportunityId: Id) {
    const opportunity = this.opportunities.get(opportunityId)
    if (!opportunity) {
      return
    }
    await this._api.opportunities
      .withdraw(Id.assert(opportunityId))
      .then(() => {
        opportunity.set({
          isApplicant: false,
        })
      })
  }
}
